import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success' | 'error' | 'warning' | 'info'
    autoHideDuration: 3000
  });

  const showNotification = useCallback((message, options = {}) => {
    setNotification({
      open: true,
      message,
      severity: options.severity || 'info',
      autoHideDuration: options.autoHideDuration ?? 3000
    });
  }, []);

  const success = useCallback((message, options = {}) => {
    showNotification(message, { ...options, severity: 'success' });
  }, [showNotification]);

  const error = useCallback((message, options = {}) => {
    showNotification(message, { ...options, severity: 'error', autoHideDuration: options.autoHideDuration ?? 5000 });
  }, [showNotification]);

  const warning = useCallback((message, options = {}) => {
    showNotification(message, { ...options, severity: 'warning' });
  }, [showNotification]);

  const info = useCallback((message, options = {}) => {
    showNotification(message, { ...options, severity: 'info' });
  }, [showNotification]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <NotificationContext.Provider value={{ success, error, warning, info, showNotification }}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
